<template>
  <section
    class="banner-carousel"
    :class="[backgroundClass, { 'banner-carousel--last': isLastModule }]"
    data-test-id="banner-carousel-module"
    :aria-label="!loading && hasHeading ? accessibleHeading : undefined"
    :aria-busy="loading || undefined"
  >
    <div class="banner-carousel__inner ca-bullet-container">
      <!-- Header + arrows. The arrows are hidden on mobile, where the design
           navigates by swiping and by the page control instead. -->
      <div class="banner-carousel__header" data-test-id="header">
        <!-- The design placeholders the label and the title while loading, which
             ModuleMessage cannot do: it has no loading state. The bars borrow the
             DS `gradient-loading`, the same one the cards below animate with. -->
        <div
          v-if="loading"
          class="banner-carousel__header-placeholder"
          data-test-id="header-placeholder"
          aria-hidden="true"
        >
          <span class="banner-carousel__label-placeholder gradient-loading" />
          <span class="banner-carousel__title-placeholder gradient-loading" />
        </div>

        <!-- ModuleMessage drops the label on its own when there is none, and
             keeps the title at the size the design asks for. SectionHeader is
             not an alternative here: it tops out at paragraph-bold. -->
        <div v-else class="flex-1 min-w-0">
          <ModuleMessage
            data-test-id="module-message"
            :label="label || undefined"
            :title="title"
            :title-tag="normalizeSemanticTextTag(titleHeadingType)"
          />
        </div>

        <div
          v-if="showArrows"
          class="banner-carousel__arrows"
          data-test-id="arrows"
        >
          <IconButton
            data-test-id="prev-button"
            size="s"
            :shadow="true"
            :type="iconButtonType"
            :disabled="!hasPrev"
            aria-label="Conteúdo anterior"
            @clicked="scrollByPage(-1)"
          >
            <template #trailingIcon>
              <IconComponent size="2" name="chev_left" />
            </template>
          </IconButton>
          <IconButton
            data-test-id="next-button"
            size="s"
            :shadow="true"
            :type="iconButtonType"
            :disabled="!hasNext"
            aria-label="Conteúdo seguinte"
            @clicked="scrollByPage(1)"
          >
            <template #trailingIcon>
              <IconComponent size="2" name="chev_right" />
            </template>
          </IconButton>
        </div>
      </div>

      <!-- The cards are a list of related contents, per the design's a11y notes.
           Native scroll-snap gives touch swiping and keyboard scrolling for free. -->
      <ul
        ref="trackRef"
        class="banner-carousel__track"
        :class="{
          'banner-carousel__track--fade-start': showStartFade,
          'banner-carousel__track--fade-end': showEndFade,
        }"
        data-test-id="track"
        :aria-label="listLabel"
        @scroll.passive="onScroll"
      >
        <li
          v-for="(card, i) in slides"
          :key="i"
          class="banner-carousel__slide"
          data-test-id="slide"
        >
          <a
            class="banner-carousel__link relative"
            :data-test-id="`card-link-${i}`"
            :href="loading ? undefined : card.url || undefined"
            :target="card.target || undefined"
            :rel="relFor(card)"
            :aria-label="loading ? undefined : accessibleName(card, i)"
            :aria-hidden="loading ? 'true' : undefined"
            :tabindex="loading ? -1 : 0"
          >
            <component
              :is="cardComponent"
              tabindex="-1"
              :theme="cardTheme"
              :is-loading="loading"
              :title="card.title"
              :title-tag="normalizeSemanticTextTag(card.titleHeadingType)"
              :sub-title="card.subtitle"
              :sub-title-tag="
                normalizeSemanticTextTag(card.subtitleHeadingType)
              "
              :image="card.imageUrl"
              action="icon"
            />
            <div
              v-if="labelPublicidade"
              id="banner-carousel-publicity"
              class="absolute top-[126px] right-[24px] !font-semibold py-size-3 px-size-4 caption-medium text-text-body bg-solarized rounded-full"
            >
              {{ labelPublicidade }}
            </div>
          </a>
        </li>
      </ul>

      <!-- Page dots (mobile only) -->
      <CaPageControl
        v-if="showDots"
        class="ca-slider-catalog__dots"
        :count="pageCount"
        :model-value="page"
        @update:model-value="goToPage"
      />
    </div>

    <!-- Announces the position without moving focus, per the design's a11y notes. -->
    <p class="sr-only" aria-live="polite" data-test-id="live-region">
      {{ liveMessage }}
    </p>
  </section>
</template>

<script setup lang="ts">
/**
 * BannerCarouselModule is the institutional highlights module: a heading, an
 * optional label, and a horizontal carousel of banner cards linking to the
 * pages configured in Sitecore.
 *
 * The card, the heading and the page control all come from the design system.
 * The track is local because the DS ContentCarousel keeps its index private,
 * so it cannot drive the page control the design asks for on mobile, and it
 * renders no list semantics.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import {
  BannerCardHorizontal,
  BannerCardVertical,
  IconButton,
  IconComponent,
  ModuleMessage,
} from "ca-online-design-system";
import CaPageControl from "../../base/CaPageControl.vue";
import { normalizeSemanticTextTag } from "../../../utils/heading";
defineOptions({
  name: "BannerCarouselModule",
});

export interface BannerCarouselCard {
  /** Url of the card image. Optional. Falls back to the DS error image. */
  imageUrl?: string;
  /** Alternative text for the image, used to help name the card. */
  imageAlt?: string;
  /** Category line, shown small and green above the headline. */
  title?: string;
  titleHeadingType?: string;
  /** Headline of the card. */
  subtitle?: string;
  subtitleHeadingType?: string;
  /** Url the card links to. */
  url?: string;
  /** Where to open the url, e.g. `"_blank"`. Optional. Defaults to the same tab. */
  target?: string;
}

interface Props {
  /** Optional pill above the module title. */
  label?: string;
  /** Module title, rendered as the section heading. */
  title?: string;
  /** Semantic element used for the module title. */
  titleHeadingType?: string;
  /** Background behind the module. */
  backgroundColor?: "none" | "green" | "solarized";
  /** Colour of the cards. Left unauthored it follows the background: white over
   * a colour, solarized over nothing. */
  cardColor?: "white" | "solarized";
  /** Which DS banner card the module lays out: the vertical card, image on top,
   * or the horizontal one, image on the side. Both show three per view. */
  orientation?: "vertical" | "horizontal";
  /** The cards, in the order they should appear. */
  cards?: BannerCarouselCard[];
  /** While true the module keeps its shape but shows placeholders. */
  loading?: boolean;
  /** Label for the publicity section. */
  labelPublicidade?: string;
  isLastModule?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  title: "",
  backgroundColor: "none",
  cardColor: undefined,
  orientation: "vertical",
  cards: () => [],
  loading: false,
  isLastModule: false,
});

/** How many placeholder cards to draw before the real ones arrive. */
const LOADING_SLIDES = 3;

const slides = computed<BannerCarouselCard[]>(() =>
  props.loading && !props.cards.length
    ? (Array.from(
        { length: LOADING_SLIDES },
        () => ({}),
      ) as BannerCarouselCard[])
    : props.cards,
);

const BACKGROUND_CLASSES = {
  none: "",
  green: "banner-carousel--green",
  solarized: "banner-carousel--solarized",
} as const;

const backgroundClass = computed(
  () => BACKGROUND_CLASSES[props.backgroundColor] ?? "",
);

const cardComponent = computed(() =>
  props.orientation === "horizontal"
    ? BannerCardHorizontal
    : BannerCardVertical,
);

/** The DS calls the white card "inverted". Without an authored colour the card
 * follows the background, as it did before Sitecore exposed the choice.
 *
 * The white card over the white background is on the author: the DS paints
 * `inverted` as a bare white fill — no border, and a shadow only on hover — so
 * over `backgroundColor: "none"` the card, its corners and the peek of the next
 * one all disappear. Left as authored on purpose; it is the combination that is
 * wrong, not the module, and following the background is already what happens
 * when no colour is chosen. */
const cardTheme = computed(() => {
  if (props.cardColor)
    return props.cardColor === "white" ? "inverted" : "default";
  return props.backgroundColor === "none" ? "default" : "inverted";
});

/** The DS icon button paints itself white over a coloured background. */
const iconButtonType = computed(() =>
  props.backgroundColor === "none" ? "theme" : "inverted",
);

/** The module has a headerless variant, and an empty heading would leave the
 * section with a blank accessible name. */
const hasHeading = computed(() => Boolean(props.label || props.title));

/** The design voices the header as "Destaques. Últimos destaques.": the label
 * first, then the title. */
const accessibleHeading = computed(() =>
  [props.label, props.title].filter(Boolean).join(". "),
);

/** Entering the list should say how many contents there are, per the design's
 * voiced preview ("Carrossel com 6 conteúdos"). */
const listLabel = computed(() => {
  const total = slides.value.length;
  if (props.loading || !total) return undefined;
  return `Carrossel com ${total} ${total === 1 ? "conteúdo" : "conteúdos"}`;
});

const relFor = (card: BannerCarouselCard) =>
  card.target === "_blank" ? "noopener noreferrer" : undefined;

/**
 * Screen readers should hear the category and the headline, never a bare
 * "Link". The image alt is appended only when it carries information.
 */
const accessibleName = (card: BannerCarouselCard, index: number) => {
  const parts = [card.title, card.subtitle, card.imageAlt].filter(Boolean);
  return `${parts.join(". ")}. Conteúdo ${index + 1} de ${slides.value.length}.`;
};

const trackRef = ref<HTMLElement | null>(null);
const page = ref(0);
const slidesToShow = ref(3);
const showStartFade = ref(false);
const showEndFade = ref(false);

const hasOverflow = computed(() => slides.value.length > slidesToShow.value);
const pageCount = computed(() =>
  Math.max(1, slides.value.length - slidesToShow.value + 1),
);
const hasPrev = computed(() => page.value > 0);
const hasNext = computed(() => page.value < pageCount.value - 1);

/** Mobile swipes and uses the page control; wider viewports use the arrows. */
const showDots = computed(
  () => !props.loading && slidesToShow.value === 1 && pageCount.value > 1,
);
const showArrows = computed(
  () => !props.loading && slidesToShow.value > 1 && hasOverflow.value,
);

const liveMessage = computed(() =>
  props.loading || !slides.value.length
    ? ""
    : `Conteúdo ${page.value + 1} de ${slides.value.length}.`,
);

/** Width of one slide plus its gap, both measured rather than assumed: the gap
 * narrows on mobile, and reading it back keeps this in step with the stylesheet. */
const step = () => {
  const track = trackRef.value;
  if (!track) return 0;
  const slide = track.querySelector<HTMLElement>(".banner-carousel__slide");
  if (!slide) return 0;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  return slide.getBoundingClientRect().width + gap;
};

/**
 * Programmatic scrolling leaves the easing to the CSS `scroll-behavior`: passing
 * `behavior: "smooth"` here fights the mandatory snapping and Chrome re-snaps
 * the track back to where it started.
 */
const scrollByPage = (direction: number) => {
  trackRef.value?.scrollBy({ left: step() * direction });
};

/** Clicking a dot scrolls the track; the scroll handler syncs the value back. */
const goToPage = (value: number) => {
  trackRef.value?.scrollTo({ left: step() * value });
};

const updateEdgeFades = (track = trackRef.value) => {
  if (!track) return;

  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  showStartFade.value = track.scrollLeft > 0;
  showEndFade.value = track.scrollLeft < maxScrollLeft - 1;
};

/** Kept synchronous: the browser already throttles scroll events, and a
 * requestAnimationFrame guard stops updating whenever its callback is starved,
 * e.g. while the tab is in the background. */
const onScroll = () => {
  const track = trackRef.value;
  const width = step();
  if (!track) return;

  updateEdgeFades(track);
  if (!width) return;

  page.value = Math.min(
    pageCount.value - 1,
    Math.round(track.scrollLeft / width),
  );
};

let tabletUpQuery: MediaQueryList | null = null;
let desktopUpQuery: MediaQueryList | null = null;

/** One card below SM, two from SM up, three from MD up. Mirrors the widths in
 * the stylesheet, which is what actually lays the slides out. */
const updateSlidesToShow = () => {
  if (desktopUpQuery?.matches) {
    slidesToShow.value = 3;
    return;
  }
  slidesToShow.value = tabletUpQuery?.matches ? 2 : 1;
};

onMounted(() => {
  tabletUpQuery = window.matchMedia("(min-width: 768px)");
  desktopUpQuery = window.matchMedia("(min-width: 1024px)");
  updateSlidesToShow();
  updateEdgeFades();
  tabletUpQuery.addEventListener("change", updateSlidesToShow);
  desktopUpQuery.addEventListener("change", updateSlidesToShow);
});

onBeforeUnmount(() => {
  tabletUpQuery?.removeEventListener("change", updateSlidesToShow);
  desktopUpQuery?.removeEventListener("change", updateSlidesToShow);
});
</script>

<style lang="scss" scoped>
/* Gaps and slide widths follow the design's responsive spec, which pins four
   viewports: Mobile 375, Tablet S 768, Desktop XS 1024 and LG 1366. Written
   mobile-first, so each query only states what that step changes.

   The side margins are not ours: `ca-bullet-container` carries the grid's own
   ladder (24/24/32/44/128/320), so only the vertical rhythm is set here. */
.banner-carousel {
  @apply relative w-full;
  padding-block: var(--size-8);
  /* Backstop for the track's negative margins, as CaSliderCatalogModule does on
     its own root. `clip` and not `hidden`: it does not make a scroll container,
     so nothing inside changes behaviour. */
  overflow-x: clip;
}

.banner-carousel--green {
  background: var(--ca-color-container-bg-container-high, #e5f4ef);
}

.banner-carousel--green.banner-carousel--last,
.banner-carousel--solarized.banner-carousel--last {
  border-bottom-right-radius: var(--size-8);
  border-bottom-left-radius: var(--size-8);
}

.banner-carousel--solarized {
  background: var(--ca-color-empresas-solarized, #f6f7f2);
}

/* Over a coloured background the module label needs the translucent grey the
   design specifies, not the solarized fill the DS `container` label theme paints
   (#f6f7f2, which all but disappears on solarized). The text already comes out as
   `text-text-body`, so only the fill is overridden. Drop this once the DS grows a
   label theme for it. */
.banner-carousel--green :deep(.label-theme-container),
.banner-carousel--solarized :deep(.label-theme-container) {
  background-color: var(--ca-color-neutrals-on-background-low-10, #646f761a);
}

.banner-carousel__inner {
  @apply mx-auto flex flex-col;
  gap: var(--size-5);
}

.banner-carousel__header {
  @apply flex items-end;
  gap: var(--size-4);
}

.banner-carousel__header :deep([data-test-id="label"]) {
  text-transform: uppercase;
}

.banner-carousel__arrows {
  @apply flex shrink-0 items-center;
  gap: var(--size-4);
}

.banner-carousel__header-placeholder {
  @apply flex flex-col;
  gap: var(--size-4);
}

.banner-carousel__label-placeholder,
.banner-carousel__title-placeholder {
  @apply block;
  height: 36px;
  max-width: 100%;
}

.banner-carousel__label-placeholder {
  width: 148px;
  border-radius: 100px;
}

.banner-carousel__title-placeholder {
  width: 428px;
  border-radius: 8px;
}

/* Native scroll-snap: touch swiping and keyboard scrolling come for free. */
.banner-carousel__track {
  @apply flex list-none overflow-x-auto;
  gap: var(--size-4);
  /* How much of the card that comes next shows on mobile. The slide width is
     what pays for it — see `.banner-carousel__slide`. */
  --peek: var(--size-4);
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
  /* Room for the cards' focus ring, which the scroll container would clip. */
  margin-block: -4px;
  padding-block: 4px;
  /* Below 1566px the track runs to the screen edges, so a card turns in from
      off-screen instead of appearing at the end of the list — the same idea as
      CaSliderCatalogModule. At wider viewports it returns to the grid below.

     The bleed is one page margin wide, pulled from the grid's own `ca-margin()`
     so the two cannot drift. Percentages looked tidier but cannot work here:
     `scroll-padding` resolves them against the scrollport, which is the bled
     track itself, so it came out 0 and the first card snapped to the screen edge
     instead of the grid. The padding puts the resting position back on the grid
     and `scroll-padding` keeps the snap there; both are wide enough to have
     replaced the 4px of focus-ring room above. */
  margin-inline: -#{ca-margin("base")};
  padding-inline: ca-margin("base");
  scroll-padding-inline: ca-margin("base");

  @include ca-bp("sm") {
    margin-inline: -#{ca-margin("sm")};
    padding-inline: ca-margin("sm");
    scroll-padding-inline: ca-margin("sm");
  }

  @include ca-bp("md") {
    margin-inline: -#{ca-margin("md")};
    padding-inline: ca-margin("md");
    scroll-padding-inline: ca-margin("md");
  }

  @include ca-bp("lg") {
    margin-inline: -#{ca-margin("lg")};
    padding-inline: ca-margin("lg");
    scroll-padding-inline: ca-margin("lg");
  }

  @include ca-bp("lg2") {
    margin-inline: -#{ca-margin("lg2")};
    padding-inline: ca-margin("lg2");
    scroll-padding-inline: ca-margin("lg2");
  }

  @include ca-bp("xl") {
    margin-inline: -#{ca-margin("xl")};
    padding-inline: ca-margin("xl");
    scroll-padding-inline: ca-margin("xl");
  }
}

@media (min-width: 1536px) {
  /* From lg2 up the track keeps the same off-grid bleed as the smaller
     breakpoints, so the next card peeks in from the right; the only difference
     here is the end mask, which fades that peek out. The peek is one page margin
     less the gap, so the fade spans exactly it. */
  .banner-carousel__track {
    --fade-end: calc(#{ca-margin("lg2")} - var(--size-5));

    @include ca-bp("xl") {
      --fade-end: calc(#{ca-margin("xl")} - var(--size-5));
    }
  }

  .banner-carousel__track--fade-start {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--size-6)
    );
    mask-image: linear-gradient(to right, transparent, #000 var(--size-6));
  }

  .banner-carousel__track--fade-end {
    -webkit-mask-image: linear-gradient(
      to right,
      #000 calc(100% - var(--fade-end)),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      #000 calc(100% - var(--fade-end)),
      transparent
    );
  }

  .banner-carousel__track--fade-start.banner-carousel__track--fade-end {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--size-6),
      #000 calc(100% - var(--fade-end)),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--size-6),
      #000 calc(100% - var(--fade-end)),
      transparent
    );
  }
}

.banner-carousel__track::-webkit-scrollbar {
  display: none;
}

/* One card wide, which on mobile is the grid width (327 at 375) less what the
   peek costs — the track pads itself back in by the margin it bled out, so
   `100%` is the grid.

   Where that cost comes from: the card starts at the margin, so the next one
   starts a gap after it ends and what shows of it is `margin - gap` — 8px, and
   free, since neither number is ours to move. Anything beyond that the card has
   to give up, hence the three terms: the margin the track bleeds into, minus the
   gap that fills it, minus the peek we want. */
.banner-carousel__slide {
  @apply min-w-0;
  flex: 0 0 calc(100% + var(--size-5) - var(--size-4) - var(--peek));
  scroll-snap-align: start;
}

.banner-carousel__link {
  @apply block h-full no-underline;
  color: inherit;
}

.banner-carousel__link :deep([data-test-id="banner-card-vertical"]),
.banner-carousel__link :deep([data-test-id="banner-card-horizontal"]) {
  @apply h-full;
}

/* Both DS cards hang the same halo on themselves — `0 0 5px 4px` with no
   offset, so about 9px of shadow on every side — and each does it its own way:
   the vertical one through the `shadow-6` class and only while the theme is
   `default`; the horizontal one baked into its base rule unconditionally, so
   `inverted` keeps it and it shows on all three backgrounds.

   The shadow is there to lift the card off a page the module does not own.
   Inside the track it only bleeds into the spacing: two neighbouring halos are
   18px across a 16px gap on mobile, so the cards touch and the row reads
   tighter than it is. Each card is already separated by its own fill, so the
   shadow buys nothing it does not also cost. */
.banner-carousel__link :deep([data-test-id="banner-card-vertical"]),
.banner-carousel__link :deep([data-test-id="banner-card-horizontal"]) {
  box-shadow: none;
}

.banner-carousel__link :deep([data-test-id="title"]) {
  text-transform: uppercase;
}

.banner-carousel__link :deep([data-test-id="title"]),
.banner-carousel__link :deep([data-test-id="subtitle"]),
.banner-carousel__link :deep([data-test-id="description"]) {
  overflow-wrap: anywhere;
}

/* Both DS cards render their chevron without an icon-class, so it inherits the
   body text colour instead of the primary green the design asks for. The icon
   paints itself with `currentColor`, so setting `color` is enough — in the vertical
   card `icon-action` is the wrapper and the value inherits down to the svg. Drop
   this once the DS ships the fix. */
.banner-carousel__link :deep([data-test-id="icon-action"]) {
  color: var(--ca-color-text-primary, #066542);
}

/* The image keeps a 12px radius in every card and every state. The vertical card
   already lands there; the horizontal one rounds its image 16px on the right and
   48px at the top left, and the shorthand below resets all four corners. 12px is a
   literal because the DS radius scale defines it as one, with no token behind it. */
.banner-carousel__link
  .banner-card-horizontal-main
  :deep([data-test-id="image-wrapper"]),
.banner-carousel__link
  .banner-card-horizontal-main
  :deep([data-test-id="loading-image"]) {
  border-radius: 48px 12px 12px 0;
}

.banner-carousel__link
  .banner-card-vertical-main
  :deep([data-test-id="image-wrapper"]),
.banner-carousel__link
  .banner-card-vertical-main
  :deep([data-test-id="loading-image"]) {
  border-radius: 12px;
}

/* Fallback for a card image that fails to load. Two things are needed: the DS
   paints the wrapper with its own error png through an inline style, so replacing
   it takes !important, and the .image child sits on top with an opaque white
   fill, which is what actually hides any fallback today. Making the child
   transparent lets the ground below show through when the url 404s. */
.banner-carousel__link :deep([data-test-id="image-wrapper"]) {
  background-color: #e4e5e7;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='9' cy='9' r='2'/%3E%3Cpath d='m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21'/%3E%3C/svg%3E") !important;
  background-size: 40px 40px;
}

.banner-carousel__link :deep([data-test-id="image"]) {
  background-color: transparent;
}

.banner-carousel__link:focus {
  @apply outline-none;
}

.banner-carousel__link:focus-visible {
  @apply outline outline-1 outline-offset-1;
  outline-color: var(--ca-color-outline-focus-only);
  border-radius: 16px;
}

.ca-slider-catalog__dots {
  @apply justify-center;
}

/* CaPageControl paints its dots with the inverted neutrals, which are white —
   they were meant for a dark background and all but vanish on the three
   backgrounds this module uses. The design asks for the grey of the neutral
   ramp on the dots that are not selected, and the primary green on the one
   that is. */
.ca-slider-catalog__dots :deep(.ca-page-control__dot) {
  background-color: var(--ca-color-neutrals-on-background-low-60, #646f7699);
}

.ca-slider-catalog__dots :deep(.ca-page-control__dot--active) {
  background-color: var(--ca-color-text-primary, #066542);
}

@media (prefers-reduced-motion: reduce) {
  .banner-carousel__track {
    scroll-behavior: auto;
  }
}

/* ModuleMessage tags its title `heading-jumbo sm:heading-large`, but the DS ships
   its institutional overrides only for the base classes. Under
   [data-theme="institutional"] those land at specificity 3 and beat the plain
   `sm:heading-large` (2), so the jumbo ladder wins at every width and the SM
   switch never happens. Restated here: jumbo keeps mobile, heading-large takes
   over from SM up. Values mirror the DS's institutional `.heading-large` ladder,
   measured from the built stylesheet. The `.banner-carousel` prefix is what puts
   this above the theme rules, and `module-message` keeps it off the card titles,
   which carry the same test id. Drop this once the DS themes its sm: variants. */
@media (min-width: 768px) {
  .banner-carousel {
    padding-block: var(--size-9);
  }

  .banner-carousel
    [data-test-id="module-message"]
    :deep([data-test-id="title"]) {
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 0.288px;
    word-spacing: 0.384px;
  }

  .banner-carousel__inner {
    gap: var(--size-7);
  }

  .banner-carousel__track {
    gap: var(--size-5);
  }

  .banner-carousel__slide {
    flex: 0 0 calc((100% - var(--size-5)) / 2);
  }
}

@media (min-width: 1024px) {
  .banner-carousel {
    padding-block: var(--size-11);
  }

  .banner-carousel
    [data-test-id="module-message"]
    :deep([data-test-id="title"]) {
    font-size: 32px;
    font-weight: 700;
    line-height: 40px;
    letter-spacing: 0.384px;
    word-spacing: 0.512px;
  }

  .banner-carousel__slide {
    flex: 0 0 calc((100% - 2 * var(--size-5)) / 3);
  }
}
/* LG is the last step of the institutional heading-large ladder. */
@media (min-width: 1366px) {
  .banner-carousel
    [data-test-id="module-message"]
    :deep([data-test-id="title"]) {
    font-size: 48px;
    font-weight: 700;
    line-height: 60px;
    letter-spacing: 0.576px;
    word-spacing: 0.768px;
  }
}
</style>
